// The Send PO window's address boxes (To / CC / BCC). Pure, so the offline
// gate executes the same code the window runs.

export interface AddressEntry {
  email: string;
  name?: string;
}

/** A supplier contact offered in the suggestion list. */
export interface AddressBookEntry {
  email: string;
  name?: string;
  /** e.g. "Art department" — shown beside the name in the suggestions. */
  role?: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What typing `raw` adds — a contact (matched by email) or a typed
 *  address — or why not. `taken` = lowercased emails already on the email. */
export function resolveAddressInput(
  raw: string,
  book: readonly AddressBookEntry[],
  taken: ReadonlySet<string>,
): { add: AddressEntry } | { error: string } {
  const email = raw.trim();
  if (!EMAIL_RE.test(email)) return { error: "Enter a valid email address." };
  if (taken.has(email.toLowerCase())) return { error: "That address is already on this email." };
  const hit = book.find((b) => b.email.toLowerCase() === email.toLowerCase());
  return { add: hit?.name ? { email: hit.email, name: hit.name } : { email: hit?.email ?? email } };
}

/** The list as it will SEND — what's typed in the box but not yet Added goes
 *  too (Stephanie 2026-10-28: an address typed without clicking "+ Add" must
 *  not be dropped). Blank box → the list as is; already on the email → as is;
 *  a box that isn't an address → an error, so the window refuses to send
 *  instead of silently leaving someone off. */
export function withTypedAddress(
  entries: AddressEntry[],
  draft: string,
  book: readonly AddressBookEntry[],
  taken: ReadonlySet<string>,
): { entries: AddressEntry[] } | { error: string } {
  if (draft.trim() === "") return { entries };
  if (taken.has(draft.trim().toLowerCase())) return { entries };
  const r = resolveAddressInput(draft, book, taken);
  return "error" in r ? { error: r.error } : { entries: [...entries, r.add] };
}
